import React, { useEffect } from 'react';

// Libs
import ContentContainer from '@libComponents/ContentContainer';

import TableCellContainer from '@libComponents/table/TableCellContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import {
    datasetLoadingSelector,
    entitySelector,
    createFetchPaginatedDataset
} from '@libs/datasets';
import EditIcon from '@mui/icons-material/EditOutlined';
import { Box, TableBody, TableRow, Typography, styled } from '@mui/material';

import { Button, Pagination } from '@libComponents';
import DialogWrapper from '@libComponents/Dialog';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
import { DOMAIN_NAME, ROWS_PER_PAGE } from './common';
import { deleteHoliday } from './redux.actions';
import { HOLIDAY } from './redux.datasets';
import { theme } from 'app/Theme';
import { DeleteOutline } from '@mui/icons-material';
import createCachedSelector from 're-reselect';
import dayjs from 'dayjs';
import { usePermission } from 'permissions';
import YearPicker from 'common/components/YearPicker';
import SelectCalendar from './SelectCalendar';
import ExportHolidaysExcel from './components/exportHolidaysExcel';

const propTypes = {};

const defaultProps = {
    entity: HOLIDAY,
    domain: DOMAIN_NAME,
    loading: true
};

const StyledBox = styled(Box)(() => ({
    width: '100%',
    overflow: 'auto',
    '& table': {
        tableLayout: 'fixed'
    }
}));

const StyledInputWrapper = styled(Box)(({ theme: { spacing } }) => ({
    display: 'flex',
    gap: spacing(2),
    marginLeft: 'auto',
    marginRight: spacing(2),
    alignItems: 'center'
}));

const TABLE_DATA = {
    'name': 'Name',
    'date': 'date',
    'description': 'Description',
    'calender': 'Calender',
    'id': 'id'
};

const totalRowsSelector = createCachedSelector(entitySelector, (resp) =>
    resp.total ? parseInt(resp.total) : 0
)((state, domain, entity) => `@data-${domain}-${entity}`);

const dataSelector = createSelector(
    (state) => entitySelector(state, DOMAIN_NAME, HOLIDAY),
    (resp) =>
        resp && resp.data
            ? resp.data.map((obj) =>
                Object.fromEntries(
                    Object.keys(TABLE_DATA).map((key) => [key, obj[key]])
                )
            )
            : []
);

const mapState = (state, { domain, entity }) => ({
    data: dataSelector(state, domain, entity),
    loading: datasetLoadingSelector(state, domain, entity),
    totalRows: totalRowsSelector(state, domain, entity)
});

const mapDispatch = (dispatch, { entity }) => ({
    fetchDataset: ({
        offset = '',
        limit = ROWS_PER_PAGE,
        shouldAppend = false,
        year,
        calenderId
    }) =>
        dispatch(
            createFetchPaginatedDataset({
                entity: entity,
                domain: DOMAIN_NAME
            })({
                limit,
                offset,
                shouldAppend,
                year,
                calenderId
            })
        )
});

const HolidaysList = connect(
    mapState,
    mapDispatch
)(({ fetchDataset, data, totalRows = 0, ...props }) => {
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        fetchDataset({
            offset: '',
            year: currentYear,
            calenderId: null // All calendars by default
        });
    }, []);

    const RXDispatch = useDispatch();
    const navigate = useNavigate();
    const [open, setOpen] = React.useState(false);
    const [payload, setSelectedPayload] = React.useState({});
    const [pageSize, setPageSize] = React.useState(ROWS_PER_PAGE);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [selectedYear, setSelectedYear] = React.useState(currentYear);
    const [selectedCalendar, setSelectedCalendar] = React.useState(null);

    // Permission checks
    const canUpdateCalender = usePermission('calender.update a calender');
    const canDeleteHoliday = usePermission('holiday.delete a holiday');
    const hasAnyActionPermission = canUpdateCalender || canDeleteHoliday;

    const handleYearChange = (value) => {
        const year = dayjs(value).year();
        setSelectedYear(year);
        setCurrentPage(1);
        fetchDataset({
            offset: 0,
            limit: pageSize,
            year: year,
            calenderId: selectedCalendar
        });
    };

    const handleCalendarChange = (calenderId) => {
        setSelectedCalendar(calenderId);
        setCurrentPage(1);
        fetchDataset({
            offset: 0,
            limit: pageSize,
            year: selectedYear,
            calenderId: calenderId
        });
    };

    const handleClick = (row) => {
        navigate(`/holiday-list/update/${row?.id}`, { state: row });
    };

    const handleDelete = () => {
        RXDispatch(deleteHoliday({ handleSubmit: deleteCB, payload }));
    };

    const deleteCB = (payload) => {
        handleClose();
    };

    const handleClickOpen = (row) => {
        setOpen(true);
        setSelectedPayload(row);
    };

    const handleClose = (value) => {
        setOpen(false);
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
        fetchDataset({
            offset: (newPage - 1) * pageSize,
            limit: pageSize,
            year: selectedYear,
            calenderId: selectedCalendar
        });
    };

    const handleRowsPerPageChange = (pageSize) => {
        setPageSize(pageSize);
        setCurrentPage(1);
        fetchDataset({
            offset: 0 * pageSize,
            limit: pageSize,
            year: selectedYear,
            calenderId: selectedCalendar
        });
    };

    if (!data) return null;
    return (
        <ContentContainer
            AdornmentComponent={
                <StyledInputWrapper>
                    <SelectCalendar
                        value={selectedCalendar}
                        onChange={handleCalendarChange}
                    />
                    <Box sx={{ width: 180 }}>
                        <YearPicker
                            onChange={handleYearChange}
                            value={selectedYear.toString()}
                            minDate={dayjs('2023-01-01')}
                            maxDate={dayjs()}
                        />
                    </Box>
                    <ExportHolidaysExcel
                        year={selectedYear}
                        calenderId={selectedCalendar}
                        totalRows={totalRows}
                    />
                </StyledInputWrapper>
            }
            title='Holiday List'
            headerBg={theme.palette.background.mainHeader}
        >
            <StyledBox>
                <TableContainer>
                    <TableHeaderContainer>
                        {Object.values(TABLE_DATA).map((th, i) => {
                            switch (th) {
                                case 'id':
                                    return;
                                default:
                                    return (
                                        <TableCellContainer
                                            sx={{
                                                fontWeight: 'bold'
                                            }}
                                            typography='caption'
                                            key={th}
                                        >
                                            {th}
                                        </TableCellContainer>
                                    );
                            }
                        })}
                        {hasAnyActionPermission && (
                            <TableCellContainer
                                align='right'
                                sx={{ pr: 16.5, fontWeight: 'bold' }}
                            >
                                Action
                            </TableCellContainer>
                        )}
                    </TableHeaderContainer>
                    <TableBody>
                        {data.map((row, i) => (
                            <TableRow key={i}>
                                {Object.keys(TABLE_DATA).map((key, index) => {
                                    switch (key) {
                                        case 'id':
                                            return;

                                        case 'calender':
                                            return (
                                                <TableCellContainer
                                                    key={index}
                                                >
                                                    {row[key]
                                                        ? row[key].name
                                                        : ''}
                                                </TableCellContainer>
                                            );
                                        case 'date':
                                            return (
                                                <TableCellContainer
                                                    key={index}
                                                >
                                                    {dayjs(row[key]).format(
                                                        'YYYY-MM-DD'
                                                    )}
                                                </TableCellContainer>
                                            );
                                        default:
                                            return (
                                                <TableCellContainer
                                                    key={index}
                                                >
                                                    {row[key]}
                                                </TableCellContainer>
                                            );
                                    }
                                })}
                                {hasAnyActionPermission && (
                                    <TableCellContainer align='right'>
                                        {canUpdateCalender && (
                                            <Button
                                                variant='outlined'
                                                sx={{ mr: 2, cursor: 'pointer' }}
                                                type='button'
                                                onClick={() => {
                                                    handleClick(row);
                                                }}
                                                color='primary'
                                                startIcon={<EditIcon color='primary' />}
                                            >
                                                Edit
                                            </Button>
                                        )}

                                        {canDeleteHoliday && (
                                            <DeleteOutline
                                                sx={{
                                                    cursor: 'pointer',
                                                    opacity: 0.5,
                                                    position: 'relative',
                                                    top: 8
                                                }}
                                                onClick={() => {
                                                    handleClickOpen(row);
                                                }}
                                            />
                                        )}
                                    </TableCellContainer>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={Math.ceil(totalRows / pageSize)}
                        onChange={handleChangePage}
                        page={currentPage}
                        sx={{ display: 'flex', justifyContent: 'center' }}
                        isRowsPerPageVarient={true}
                        rowsPerPageProps={{
                            name: 'attendance',
                            onChange: handleRowsPerPageChange,
                            value: pageSize
                        }}
                    />
                </Box>
            </StyledBox>

            <DialogWrapper
                open={open}
                onClose={handleClose}
                sx={{ textAlign: 'center' }}
            >
                <Typography variant='h6' sx={{ mb: 4 }}>
                    Do you want to delete the holiday?
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2
                    }}
                >
                    <Button onClick={handleClose} color='tertiary'>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete}>Delete</Button>
                </Box>
            </DialogWrapper>
        </ContentContainer>
    );
});

HolidaysList.propTypes = propTypes;
HolidaysList.defaultProps = defaultProps;

export default HolidaysList;
